'use client';

import { type FormEvent, useEffect, useState } from 'react';
import {
  ActionIcon,
  Button,
  Modal,
  Text,
  Textarea,
  Title,
} from 'rizzui';
import { PiCheckCircleBold, PiFlashlightBold, PiXBold } from 'react-icons/pi';
import { createCustomerShipmentBookNow, type PublicShipment } from '@/server/api';

function formatLocation(location?: { city: string | null; state: string | null }) {
  if (!location) return '—';
  return [location.city, location.state].filter(Boolean).join(', ') || '—';
}

function formatPrice(value: number | null) {
  return value === null
    ? 'Call for rate'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(value);
}

function formatPickupDate(value?: string | number | null) {
  if (!value) return 'Date TBD';
  const date =
    typeof value === 'number'
      ? new Date(value < 10_000_000_000 ? value * 1000 : value)
      : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date TBD';
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatEquipment(load?: PublicShipment | null) {
  if (!load) return 'Equipment not specified';
  return (
    load.freight?.equipment_type?.description ||
    load.freight?.equipment_type?.code ||
    load.freight?.description ||
    'Equipment not specified'
  );
}

type CustomerBookModalProps = {
  load: PublicShipment | null;
  isOpen: boolean;
  onClose: () => void;
  onBookSubmitted: () => void;
};

export function CustomerBookModal({
  load,
  isOpen,
  onClose,
  onBookSubmitted,
}: CustomerBookModalProps) {
  const [message, setMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (load) {
      setMessage('');
      setSubmitError('');
      setIsSubmitting(false);
      setIsSubmitted(false);
    }
  }, [load]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!load) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await createCustomerShipmentBookNow(load.pro, {
        message: message.trim() || undefined,
      });

      if (!response.data?.success) {
        throw new Error(
          response.data?.message || 'The load could not be booked at this time.'
        );
      }

      setIsSubmitted(true);
      onBookSubmitted();
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'The load could not be booked at this time.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customSize="640px"
      rounded="xl"
      overlayClassName="bg-[#06162b]/55 backdrop-blur-[3px]"
      containerClassName="available-load-book-modal !w-[calc(100vw-2rem)] overflow-hidden border border-white/10 bg-white p-0 shadow-[0_32px_90px_rgba(4,20,40,0.32)] sm:!w-[calc(100vw-3rem)] lg:!w-[640px]"
      className="z-[10020]"
    >
      <header className="available-load-book-modal__header flex items-start justify-between gap-4 border-b border-[#e5ebf2] bg-[#f7fbff] px-5 py-5 sm:px-6">
        <div className="flex min-w-0 items-start gap-3.5">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#081e37] text-white shadow-[0_8px_22px_rgba(8,30,55,0.18)]">
            <PiFlashlightBold className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <Title
              as="h2"
              className="text-xl font-semibold tracking-[-0.035em] text-[#081e37]"
            >
              Book Load Now
            </Title>
            <Text className="mt-1 text-sm leading-5 text-[#68778a]">
              Instantly request and secure this load at the listed rate.
            </Text>
          </div>
        </div>
        <ActionIcon
          variant="text"
          onClick={onClose}
          className="!text-[#68778a] hover:!bg-transparent hover:!text-[#081e37]"
        >
          <PiXBold className="h-6 w-6" />
        </ActionIcon>
      </header>

      {isSubmitted ? (
        <div className="available-load-book-modal__success flex flex-col items-center justify-center px-5 py-10 text-center sm:px-6">
          <span className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400">
            <PiCheckCircleBold className="h-8 w-8" />
          </span>
          <Title
            as="h3"
            className="mt-4 text-2xl font-semibold tracking-[-0.035em] text-[#081e37]"
          >
            Booking Confirmed
          </Title>
          <Text className="mt-2 max-w-md text-[15px] leading-6 text-[#68778a]">
            Your booking request for load #{load?.pro} has been recorded. Our team will finalize dispatch details.
          </Text>
          <Button
            onClick={onClose}
            className="mt-6"
          >
            Done
          </Button>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="available-load-book-modal__form grid grid-cols-1 gap-5 px-5 py-6 sm:px-6"
        >
          {load && (
            <div className="available-load-book-modal__location grid grid-cols-1 gap-1.5 text-sm rounded-xl border border-[#dbe6f0] bg-[#f7fbff] p-4">
              <div className="flex items-center justify-between">
                <Text className="font-semibold text-[#081e37]">
                  Load #{load.pro}
                </Text>
                <Text className="text-base font-bold text-emerald-600">
                  {formatPrice(load.price)}
                </Text>
              </div>
              <Text className="text-slate-700">
                <span className="font-medium text-slate-500">Route: </span>
                {formatLocation(load.pickup)} → {formatLocation(load.destination)}
              </Text>
              <div className="flex flex-wrap gap-x-4 text-xs text-[#68778a]">
                <span>
                  Pickup: {formatPickupDate(load.estimated_times?.pickup_at)}
                </span>
                <span>
                  Equipment: {formatEquipment(load)}
                </span>
              </div>
            </div>
          )}

          <div className="available-load-book-modal__message">
            <Textarea
              label="Message (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Optional notes or instructions for dispatch..."
              rows={3}
            />
          </div>

          {submitError && (
            <div className="available-load-book-modal__error rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              {submitError}
            </div>
          )}

          <div className="available-load-book-modal__actions flex items-center justify-end gap-3 border-t border-[#e5ebf2] pt-5">
            <Button
              variant="outline"
              onClick={onClose}
              type="button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Booking...' : 'Confirm Booking'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

